import { useCallback, useMemo, useState } from 'react';

// Third party
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LoadingButton from '@mui/lab/LoadingButton';
import { useMediaQuery, useTheme } from '@mui/material';

import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';

// Components
import Icon from '@mdi/react';
import { mdiAsterisk } from '@mdi/js';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import { useQueryClient } from '@tanstack/react-query';
import { Iconify } from '@mgm/common/components';
import {
  BaseDrawerTitle,
  BaseDrawer,
  BaseTooltip,
  BaseLabel,
  BaseRadioGroup,
  BaseCheckbox,
  BaseCombobox,
  CustomSingleSelect,
} from '@mgm/common/components/base';
import { CustomTextField } from '@mgm/common/components/form';
import { BASE_COLORS } from '@mgm/common/utils';
import { usePostHog } from 'posthog-js/react';
import {
  BaseSelect,
} from '../../../../components/base';
import {
  EmailField, NumericField, PhoneField, TCreateEditForm,
} from '../../../../components/form';
import { DeleteIcon } from './DeleteIcon';
import { AddMemberOptions } from './AddMemberOptions';

// Hooks
import { useOptions } from '../../../../hooks/useOptions';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { useAddMembersAPI, useCommonAPI, useUpdateMemberAPI } from '../../../../hooks/api';
import { usePlatformData } from '../../../../hooks/usePlatformData';
import { useFormRules } from '../../../../hooks/useForm';
import { useEditMember, useTeamPermissions } from '../../hooks';

// Redux-toolkit
import { getIsOpenPagesNav, getPlatformData } from '../../../../store/common/selectors';
import { memberActions } from '../../../../store/team/slice';
import { commonActions } from '../../../../store/common/slice';

// Types
import {
  TCommissionPlan, TCurrency, TLocation, TMember,
} from '../../../../types';

// Data
import { DEFAULT_CURRENCY } from '../../../../data';
import { useCommissions } from '../../../../hooks/useCommissions';
import { getCommissionPlans } from '../../../../store/commissions/selectors';
import { useCommissionPlansPermissions } from '../../../commissions/hooks/useCommissionPlansPermissions';
import { getMembers } from '../../../../store/team/selectors';
import { QUERY_KEYS } from '../../../../utils/enums';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';

const CreateEditMember = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const members = useAppSelector(getMembers);
  const queryClient = useQueryClient();

  const { loadLocations } = useCommonAPI();
  const { mutate: editMutate, isLoading: isUpdateMemberLoading } = useUpdateMemberAPI();
  const { mutate: addMembersMutate, isLoading: isAddMembersLoading } = useAddMembersAPI();
  const {
    setFormInputs,
    mode,
    formInputs,
    isEdited,
  } = useEditMember();

  const commissionPlans = useAppSelector(getCommissionPlans);
  const { teamPermissions } = useTeamPermissions();

  const [sendInviteEmails, setSendInviteEmails] = useState(true);

  const {
    roleOptions,
    paymentTypesOptions,
    roleMap,
    wageMap,
  } = useOptions();

  const {
    addNewDepartment,
    addNewPosition,
    getPositions,
    getDepartments,
    deletePosition,
    deleteDepartment,
  } = usePlatformData();

  const { createCommission } = useCommissions();

  const { isExists } = useFormRules();

  const dispatch = useAppDispatch();

  const { t } = useTranslation(['inputs', 'common', 'welcome', 'team', 'settings', 'commissions']);
  const platformData = useAppSelector(getPlatformData);
  const isOpenPagesNav = useAppSelector(getIsOpenPagesNav);
  const { personnel } = useAppSelector(getAuthenticateUser);
  const posthog = usePostHog();
  const { commissionPlansPermissions } = useCommissionPlansPermissions();

  const methods = useForm<Exclude<TCreateEditForm,
  'password' | 'fullName' | 'position' | 'department' | 'commissionTags'>>({
    mode: 'all',
    values: {
      wage: formInputs.paymentAmount,
      firstName: formInputs.firstName,
      lastName: formInputs.lastName,
      email: formInputs.email,
      phone: formInputs.phone?.e164 || '',
      shopifyId: formInputs.shopifyId || '',
      sickLeave: formInputs.sickLeaveLimit || 0,
      timeoff: formInputs.timeoffLimit || 0,
      pinCode: formInputs?.pinCode || '',
      commissionPlan: (formInputs.commissionPlan || null) as TCommissionPlan,
      commissionTags: formInputs?.commissionTags || [],
    },
  });

  const dataByMode = useMemo(() => {
    const isCreateMode = mode === 'create';
    const isViewMode = mode === 'view';

    const actionTitle = isCreateMode ? t('addTeamMember', { ns: 'team' }) : t('editTeamMember', { ns: 'team' });

    return {
      title: isViewMode ? 'View a team member' : actionTitle,
      mainBtnText: isCreateMode ? t('addMember', { ns: 'team' }) : t('saveChanges', { ns: 'settings' }),
      isCreateMode,
      isViewMode,
    };
  }, [mode]);

  const handleFormChange = useCallback((key: string, value: unknown) => {
    setFormInputs((prevState) => ({
      ...prevState, [key]: value,
    }));
  }, []);

  const handleClockChange = useCallback((key: string, value: unknown) => {
    setFormInputs((prevState) => ({
      ...prevState,
      clockIn: {
        ...prevState.clockIn,
        [key]: value,
      },
    }));
  }, []);

  const addMember = async () => {
    if (methods.formState.isValid) {
      const newMember = {
        isRegistered: true,
        email: formInputs.email,
        phone: formInputs.phone,
        name: `${formInputs.firstName} ${formInputs.lastName}`,
        paymentCurrencyId: formInputs.currency?.id || DEFAULT_CURRENCY.id,
        permissionLevel: roleMap.toKey(formInputs.permissionLevel as 0 | 1 | 2),
        paymentType: wageMap.toKey(formInputs.paymentType as 0 | 1),
        paymentAmount: Number(formInputs.paymentAmount),
        locations: formInputs.locations,
        department: formInputs.department,
        position: formInputs.position,
        shopifyId: formInputs.shopifyId,
        timeoffLimit: formInputs.timeoffLimit,
        sickLeaveLimit: formInputs.sickLeaveLimit,
        pinCode: formInputs.pinCode,
        commissionPlan: formInputs.commissionPlan,
        clockIn: formInputs.clockIn,
        commissionTags: formInputs?.commissionTags?.map((tag) => tag.name) || [],
        isPromoterCommission: formInputs?.isPromoterCommission || false,
        sendInviteEmails,
      };

      const data = {
        newMember,
        sendInviteEmails,
      };

      addMembersMutate(data);
      await queryClient.removeQueries([QUERY_KEYS.ORDERS.GET_ORDERS]);
    }
  };

  const editMember = async (formData: TCreateEditForm) => {
    if (methods.formState.isValid) {
      const editedMember = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email || '',
        permissionLevel: roleMap.toKey(formInputs.permissionLevel as 0 | 1 | 2),
        paymentType: wageMap.toKey(formInputs.paymentType as 0 | 1),
        paymentAmount: Number(formInputs.paymentAmount),
        paymentCurrencyId: formInputs.currency?.id,
        department: formInputs.department,
        position: formInputs.position,
        phone: {
          e164: formData.phone,
        },
        locations: formInputs.locations,
        shopifyId: formInputs.shopifyId,
        timeoffLimit: formInputs.timeoffLimit,
        sickLeaveLimit: formInputs.sickLeaveLimit,
        pinCode: formInputs.pinCode,
        commissionPlan: formInputs.commissionPlan as TCommissionPlan,
        clockIn: formInputs.clockIn,
        commissionTags: formInputs?.commissionTags?.map((tag) => tag.name) || [],
        isPromoterCommission: formInputs?.isPromoterCommission || false,
      };

      editMutate(editedMember);
      await queryClient.removeQueries([QUERY_KEYS.ORDERS.GET_ORDERS]);
    }
  };

  const goToCommission = () => {
    createCommission();
  };

  const handleCloseNav = () => {
    dispatch(commonActions.setIsOpenPagesNav(false));

    setTimeout(() => {
      dispatch(memberActions.setSelectedMember({} as TMember));
    }, 300);
  };

  const addNewTag = (tag: string) => {
    const commissionTag = {
      name: tag,
      id: (formInputs.commissionTags?.length || 0) + 1,
    };
    setFormInputs((prevState) => ({
      ...prevState,
      commissionTags: [...(prevState?.commissionTags || []), commissionTag],
    }));
  };

  const isTagExists = (tag: string) => {
    if (!tag?.trim()) return false;

    return (
      members.some((member) => member.commissionTags?.some(
        (t) => t.toLowerCase() === tag.toLowerCase(),
      ))
      || formInputs.commissionTags?.some(
        (t) => t.name?.toLowerCase() === tag.toLowerCase(),
      )
    );
  };

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  const openShopifyIdVideo = () => {
    posthog.capture('ManageMate_web', {
      shopName: personnel?.shopName,
      email: personnel?.email,
      addedAt: new Date().toISOString(),
      action: 'user_opened_shopify_id_video',
    });

    dispatch(memberActions.setIsOpenShopifyIdVideo(true));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 650,
        variant: 'persistent',
      }}
      onCloseDrawer={onCloseDrawer}
      open={isOpenPagesNav}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={dataByMode.title} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={dataByMode.isCreateMode ? methods.handleSubmit(addMember) : methods.handleSubmit(editMember)}
        >
          <Grid paddingTop={2} container spacing={4}>
            <Grid size={{
              xs: 12,
              sm: 6,
              md: 6,
              lg: 6,
            }}
            >
              <CustomTextField
                onChange={(firstName) => {
                  handleFormChange('firstName', firstName);
                }}
                disabled={dataByMode.isViewMode}
                isRequiredField
                placeholder={t('enterFirstName')}
                labelText={t('firstName')}
                name="firstName"
              />
            </Grid>
            <Grid size={{
              xs: 12,
              sm: 6,
              md: 6,
              lg: 6,
            }}
            >
              <CustomTextField
                onChange={(lastName) => {
                  handleFormChange('lastName', lastName);
                }}
                disabled={dataByMode.isViewMode}
                isRequiredField
                placeholder={t('enterLastName')}
                labelText={t('lastName')}
                name="lastName"
              />
            </Grid>
            <Grid size={{
              xs: 12,
              sm: 6,
              md: 6,
              lg: 6,
            }}
            >
              <EmailField
                onChange={(email) => handleFormChange('email', email)}
                isRequired
                disabled={dataByMode.isViewMode}
                readOnly={mode !== 'create'}
              />
            </Grid>
            <Grid size={{
              xs: 12,
              sm: 6,
              md: 6,
              lg: 6,
            }}
            >
              <BaseLabel letterSpacing="-0.5px">{t('contactInfo')}</BaseLabel>
              <PhoneField
                disabled={dataByMode.isViewMode}
                onChange={(phone) => {
                  handleFormChange('phone', phone);
                }}
              />
            </Grid>
            <Grid size={{
              xs: 12,
              sm: 6,
              md: 6,
              lg: 6,
            }}
            >
              <BaseRadioGroup
                isDisabled={dataByMode.isViewMode || formInputs?.email === personnel.email}
                changeValue={(role) => handleFormChange('permissionLevel', Number(role))}
                value={formInputs.permissionLevel}
                label={t('permissions')}
                items={roleOptions}
              />
            </Grid>
            <Grid size={{
              xs: 12,
              sm: 6,
              md: 6,
              lg: 6,
            }}
            >
              <div className="justify-space-between">
                <div className="align-center">
                  <BaseLabel letterSpacing="-0.5px">{t('locations')}</BaseLabel>
                  <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
                </div>
                <FontAwesomeIcon
                  onClick={() => loadLocations.mutate()}
                  color={theme.palette.primary.main}
                  spin={loadLocations.isLoading}
                  className="cursor-pointer"
                  icon={['fas', 'rotate']}
                />
              </div>
              <BaseCombobox<TLocation>
                multiple
                variant="outlined"
                disabled={dataByMode.isViewMode}
                value={formInputs?.locations}
                options={platformData?.locations}
                onChange={(locations) => handleFormChange('locations', locations)}
              />
            </Grid>
            <Grid
              sx={{
                paddingBottom: { xs: 0 },
              }}
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <BaseLabel letterSpacing="-0.5px">{t('departments')}</BaseLabel>
              <BaseSelect
                selectProps={{
                  items: getDepartments,
                  variant: 'outlined',
                  defaultValue: formInputs.department,
                  onChange: (e) => handleFormChange('department', e.target.value),
                }}
                disabled={dataByMode.isViewMode}
                leftIcon={DeleteIcon}
                onLeftIconClick={deleteDepartment}
              />
              {!dataByMode.isViewMode && (
              <AddMemberOptions
                isExists={(data: string) => isExists(data, platformData.departments)}
                setOption={(department) => {
                  addNewDepartment(department);
                  methods.resetField('department');
                }}
                errorMsg="keyAlreadyExists"
                options={platformData.departments}
                rules={{
                  isExists: (data: string) => isExists(data, platformData.departments),
                }}
                labelKey="addNewDepartment"
                name="department"
              />
              )}
            </Grid>
            <Grid
              sx={{
                paddingBottom: { xs: 0 },
              }}
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <BaseLabel letterSpacing="-0.5px">{t('positions')}</BaseLabel>
              <BaseSelect
                selectProps={{
                  variant: 'outlined',
                  items: getPositions,
                  defaultValue: formInputs.position,
                  onChange: (e) => handleFormChange('position', e.target.value),
                }}
                disabled={dataByMode.isViewMode}
                leftIcon={DeleteIcon}
                onLeftIconClick={deletePosition}
              />
              {!dataByMode.isViewMode && (
              <AddMemberOptions
                options={platformData.positions}
                isExists={(data: string) => isExists(data, platformData.positions)}
                setOption={(position) => {
                  addNewPosition(position);
                  methods.resetField('position');
                }}
                errorMsg="keyAlreadyExists"
                rules={{
                  isExists: (data: string) => isExists(data, platformData.positions),
                }}
                labelKey="addNewPosition"
                name="position"
              />
              )}
            </Grid>
            <Grid size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
            }}
            >
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            <Grid
              sx={{
                paddingB: { xs: 0 },
              }}
              size={{
                sm: 12, md: 12, lg: 12, xs: 12,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'color.secondary',
                }}
              >
                {t('workClock', { ns: 'team' })}
              </Typography>
            </Grid>
            <Grid
              sx={{
                paddingBottom: { xs: 0 },
              }}
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <FormControlLabel
                sx={{
                  pr: 2,
                }}
                control={(
                  <BaseCheckbox
                    handleClick={() => handleClockChange('web', !formInputs.clockIn?.web)}
                    value={formInputs.clockIn?.web}
                    checked={formInputs.clockIn?.web}
                  />
                )}
                label={t('allowWebClockIn', { ns: 'team' })}
              />
            </Grid>
            <Grid
              sx={{
                paddingBottom: { xs: 0 },
              }}
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <FormControlLabel
                sx={{
                  pr: 2,
                }}
                control={(
                  <BaseCheckbox
                    handleClick={() => handleClockChange('mobile', !formInputs.clockIn?.mobile)}
                    value={formInputs.clockIn?.mobile}
                    checked={formInputs.clockIn?.mobile}
                  />
                )}
                label={t('allowMobileClockIn', { ns: 'team' })}
              />
            </Grid>
            <Grid size={{
              xs: 12,
              sm: 12,
              md: 12,
              lg: 12,
            }}
            >
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            <Grid
              sx={{
                paddingB: { xs: 0 },
              }}
              size={{
                sm: 12, md: 12, lg: 12, xs: 12,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'color.secondary',
                }}
              >
                {t('commissions', { ns: 'commissions' })}
              </Typography>
            </Grid>
            <Grid
              sx={{
                paddingBottom: { xs: 0 },
              }}
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <BaseLabel letterSpacing="-0.5px">{t('commissionPlan', { ns: 'commissions' })}</BaseLabel>
              <CustomSingleSelect
                options={commissionPlans}
                onChange={(data) => handleFormChange('commissionPlan', data as TCommissionPlan)}
                name="commissionPlan"
                value={formInputs.commissionPlan as TCommissionPlan}
              />
              {!dataByMode.isViewMode && commissionPlansPermissions.isAllowManage && (
                <Box pt={2} display="flex" justifyContent="flex-end">
                  <Button
                    onClick={goToCommission}
                    aria-label="add new commission plan"
                  >
                    +
                    {' '}
                    {t('addANewCommission', { ns: 'commissions' })}
                  </Button>
                </Box>
              )}
            </Grid>
            <Grid
              sx={{
                paddingBottom: { xs: 0 },
              }}
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <BaseTooltip tooltipText={t('ifAnIncomingOrder', { ns: 'team' })}>
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <BaseLabel letterSpacing="-0.5px">{t('commissionTags', { ns: 'team' })}</BaseLabel>
                  <Iconify
                    icon="material-symbols:help-outline"
                    sx={{
                      color: '#A855F7',
                      pb: 0.4,
                    }}
                  />
                </Stack>
              </BaseTooltip>
              <BaseCombobox
                multiple
                variant="outlined"
                disabled={dataByMode.isViewMode}
                value={formInputs?.commissionTags || []}
                options={formInputs.commissionTags}
                onChange={(tags) => handleFormChange('commissionTags', tags)}
                maxDisplay={isMobile ? 1 : 2}
              />
              {!dataByMode.isViewMode && (
                <AddMemberOptions
                  errorMsg="theTagExists"
                  options={formInputs.commissionTags}
                  isExists={(data) => isTagExists(data)}
                  setOption={(tag) => {
                    addNewTag(tag);
                    methods.resetField('commissionTags');
                  }}
                  rules={{
                    isExists: (data: string) => isTagExists(data),
                  }}
                  labelKey="AddCustomTag"
                  name="customTags"
                />
              )}
            </Grid>
            <Grid
              paddingBottom={0}
              size={{
                sm: 6,
                md: 6,
                lg: 6,
                xs: 12,
              }}
            >
              <BaseLabel letterSpacing="-0.5px">Shopify id</BaseLabel>
              <NumericField
                disabled={dataByMode.isViewMode}
                borderRadius={1}
                onChange={(amount) => handleFormChange('shopifyId', amount)}
                name="shopifyId"
              />
              <Button
                sx={{
                  mt: 1,
                }}
                variant="text"
                onClick={openShopifyIdVideo}
              >
                {t('watchSetupGuide')}
              </Button>
            </Grid>
            <Grid
              paddingBottom={0}
              size={{
                sm: 6,
                md: 6,
                lg: 6,
                xs: 12,
              }}
            >
              <BaseTooltip tooltipText={(
                <Typography variant="caption">
                  {t('promoterCommissionsAreCalculated', { ns: 'commissions' })}
                  {' '}
                  {' '}
                  <Typography variant="caption" fontWeight={600}>{t('duringTheirShifts', { ns: 'commissions' })}</Typography>
                  ,
                  {' '}
                  {t('ThisIsUsuallyUsed', { ns: 'commissions' })}
                </Typography>
              )}
              >
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <BaseLabel letterSpacing="-0.5px">{t('promoters', { ns: 'commissions' })}</BaseLabel>
                  <Iconify
                    icon="material-symbols:help-outline"
                    sx={{
                      color: '#A855F7',
                      pb: 0.4,
                    }}
                  />
                </Stack>
              </BaseTooltip>
              <FormControlLabel
                sx={{
                  pr: 2,
                }}
                control={(
                  <BaseCheckbox
                    handleClick={() => handleFormChange('isPromoterCommission', !formInputs.isPromoterCommission)}
                    value={formInputs.isPromoterCommission}
                    checked={formInputs.isPromoterCommission}
                  />
                )}
                label={t('promoterCommission', { ns: 'commissions' })}
              />
            </Grid>
            <Grid size={{
              sm: 12, md: 12, lg: 12, xs: 12,
            }}
            >
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            <Grid
              sx={{
                paddingB: { xs: 0 },
              }}
              size={{
                sm: 12, md: 12, lg: 12, xs: 12,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'color.secondary',
                }}
              >
                POS
              </Typography>
            </Grid>
            <Grid
              sx={{
                paddingBottom: { xs: 0 },
              }}
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <FormControlLabel
                sx={{
                  pr: 2,
                }}
                control={(
                  <BaseCheckbox
                    handleClick={() => handleClockChange('pos', !formInputs.clockIn?.pos)}
                    value={formInputs.clockIn?.pos}
                    checked={formInputs.clockIn?.pos}
                  />
                )}
                label={t('allowPosClockIn', { ns: 'team' })}
              />
            </Grid>
            {teamPermissions.isAllowViewSalaryData && (
              <Grid
                paddingBottom={0}
                size={{
                  sm: 6, md: 6, lg: 6, xs: 12,
                }}
              >
                <BaseLabel letterSpacing="-0.5px">{`${t('pinCode', { ns: 'team' })} (POS)`}</BaseLabel>
                <NumericField
                  disabled={dataByMode.isViewMode}
                  borderRadius={1}
                  maxLength={4}
                  onChange={(amount) => handleFormChange('pinCode', amount)}
                  name="pinCode"
                />
              </Grid>
            )}
            <Grid size={{
              sm: 12, md: 12, lg: 12, xs: 12,
            }}
            >
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            {teamPermissions.isAllowViewSalaryData && (
            <Grid
              sx={{
                paddingB: { xs: 0 },
              }}
              size={{
                sm: 12, md: 12, lg: 12, xs: 12,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'color.secondary',
                }}
              >
                {t('payment')}
              </Typography>
            </Grid>
            )}
            {teamPermissions.isAllowViewSalaryData && (
            <Grid
              paddingTop={0}
              size={{
                sm: 3, md: 3, lg: 3, xs: 12,
              }}
            >
              <BaseLabel letterSpacing="-0.5px">{t('wage')}</BaseLabel>
              <NumericField
                disabled={dataByMode.isViewMode}
                borderRadius={1}
                onChange={(amount) => handleFormChange('paymentAmount', amount)}
                name="wage"
              />
            </Grid>
            )}
            {teamPermissions.isAllowViewSalaryData && (
            <Grid
              paddingTop={0}
              size={{
                sm: 4, md: 4, lg: 4, xs: 12,
              }}
            >
              <BaseRadioGroup
                changeValue={(wage) => handleFormChange('paymentType', Number(wage))}
                value={formInputs.paymentType}
                isDisabled={dataByMode.isViewMode}
                label={t('type', { ns: 'common' })}
                items={paymentTypesOptions}
              />
            </Grid>
            )}
            {teamPermissions.isAllowViewSalaryData && (
            <Grid
              paddingTop={0}
              size={{
                sm: 5, md: 5, lg: 5, xs: 12,
              }}
            >
              <BaseLabel letterSpacing="-0.5px">{t('currency')}</BaseLabel>
              <CustomSingleSelect
                options={platformData.currencies}
                name="currency"
                disabled={dataByMode.isViewMode}
                value={formInputs.currency}
                isSorted
                onChange={(data) => setFormInputs((prevState) => ({
                  ...prevState,
                  currency: data as TCurrency,
                }))}
              />
            </Grid>
            )}
            <Grid size={{
              sm: 12, md: 12, lg: 12, xs: 12,
            }}
            >
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            {!isMobile && (
            <Grid
              sx={{
                paddingB: { xs: 0 },
              }}
              size={{
                sm: 8, md: 8, lg: 8, xs: 8,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'color.secondary',
                }}
              >
                {t('timeoff', { ns: 'team' })}
              </Typography>
            </Grid>
            )}
            {isMobile && (
            <Grid
              sx={{
                paddingB: { xs: 0 },
              }}
              size={{
                sm: 4, md: 4, lg: 4, xs: 4,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'color.secondary',
                }}
              >
                {t('timeoff', { ns: 'team' })}
              </Typography>
            </Grid>
            )}
            <Grid
              size={{
                sm: 6, md: 6, lg: 6, xs: 12,
              }}
              paddingTop={0}
            >
              <BaseLabel letterSpacing="-0.5px">{t('timeoffLimit', { ns: 'team' })}</BaseLabel>
              <NumericField
                disabled={dataByMode.isViewMode}
                borderRadius={1}
                onChange={(amount) => handleFormChange('timeoffLimit', amount)}
                name="timeoff"
              />
            </Grid>
            <Grid
              paddingBottom={6}
              size={{
                sm: 6, md: 6, lg: 6, xs: 12,
              }}
              paddingTop={0}
            >
              <BaseLabel letterSpacing="-0.5px">{t('sickLeaveLimit', { ns: 'team' })}</BaseLabel>
              <NumericField
                disabled={dataByMode.isViewMode}
                borderRadius={1}
                onChange={(amount) => handleFormChange('sickLeaveLimit', amount)}
                name="sickLeave"
              />
            </Grid>
            {!dataByMode.isViewMode && (
            <Grid
              paddingBottom={2}
              size={{
                sm: dataByMode.isCreateMode ? 10 : 6,
                md: dataByMode.isCreateMode ? 10 : 6,
                lg: dataByMode.isCreateMode ? 10 : 6,
                xs: 12,
              }}
              paddingTop={0}
            >
              <Stack direction="row" spacing={2}>
                <LoadingButton
                  aria-label="add team submit"
                  loading={isUpdateMemberLoading || isAddMembersLoading}
                  disabled={!methods.formState.isValid || !formInputs.locations?.length || !isEdited}
                  type="submit"
                  size="large"
                  variant="contained"
                  sx={{ flexGrow: 1 }}
                >
                  {dataByMode.mainBtnText}
                </LoadingButton>
                {dataByMode.isCreateMode && (
                <FormControlLabel
                  sx={{
                    pr: 2,
                  }}
                  control={(
                    <BaseCheckbox
                      handleClick={() => setSendInviteEmails(!sendInviteEmails)}
                      value={sendInviteEmails}
                      checked={sendInviteEmails}
                    />
                )}
                  label={t('sendRegistrationInviteViaEmail', { ns: 'team' })}
                />
                )}
              </Stack>
            </Grid>
            )}
          </Grid>
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  CreateEditMember,
};
